import * as React from 'react';
import strings from '../../../strings/strings.json';
import styled from 'styled-components';
import useModifyRequest from 'hooks/useModifyRequest';
import { useNavigate } from 'react-router-dom';
import { logError } from 'lib/debug-helpers';
import { sizes } from 'theme';
import Notification, { NotificationType } from '../../../components/notifiers/Notification';
import Button from '../../../components/buttons/Button';
import { BuildCourseContext } from 'contextProviders/BuildCourseContext';
import { AuthContext } from 'contextProviders/AuthContext';
import { RoleType } from 'models/User';
import { FlexColLg } from 'components/Elements';

type PublishCourseWarningProps = {
    setModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
};

export default function PublishCourseWarning({ setModalOpen }: PublishCourseWarningProps): JSX.Element {
    const { course } = React.useContext(BuildCourseContext);
    const { userData } = React.useContext(AuthContext);
    const navigate = useNavigate();
    const [error, setError] = React.useState<boolean>(false);

    const { modifyData: publishCourse, loading } = useModifyRequest(`courses/${course?.id}/publish`, 'POST');

    if (!course) return <></>;

    const onPublish = async () => {
        setError(false);

        const response = await publishCourse({ courseId: course.id });

        if (response.errors) {
            logError(response.errors);
            setError(true);
        } else {
            if (userData.activeRole === RoleType.ORGANISATION_ADMIN) {
                navigate(`/organisation/dashboard`, { state: { activeTabId: 'courses', activeRowId: course.id } });
            } else {
                navigate(`/trainer/dashboard/${course.id}`, { state: { activeTabId: 'courses' } });
            }
        }
    };

    return (
        <Container>
            <Notification
                notificationType={NotificationType.INFO}
                title={strings.publishCourseWarningPage.publishWarning.title}
                description={strings.publishCourseWarningPage.publishWarning.description}
                maxWidth="46rem"
            />
            <FlexColLg>
                {error && (
                    <Notification
                        notificationType={NotificationType.DANGER}
                        title={strings.publishCourseAlert.alertErrorTitle}
                        description={strings.publishCourseAlert.alertErrorDescription}
                        maxWidth="46rem"
                    />
                )}
                <ButtonRow>
                    <Button
                        onClick={() => setModalOpen(false)}
                        title={strings.publishCourseWarningPage.cancelButton}
                        aria={strings.publishCourseWarningPage.cancelButtonAria}
                        disabled={loading}
                    />
                    <Button
                        onClick={onPublish}
                        title={strings.publishCourseWarningPage.publishButtonTitle}
                        aria={strings.publishCourseWarningPage.publishButtonTitleAria}
                        disabled={loading}
                        colour='PRIMARY'
                    />
                </ButtonRow>
            </FlexColLg>
        </Container>
    );
}
const Container = styled.div`
    width: 100%;
    justify-content: center;
    display: flex;
    flex-direction: column;
    gap: 24px;
    gap: ${sizes.spacingRg};
`;

const ButtonRow = styled.div`
    display: flex;
    justify-content: flex-end;
    gap: ${sizes.spacingMd};
`;
